import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { AccessRoleDTO, AccessRoleFilterDTO, CompanyDTO, CompanyFilterDTO, PeopleDTO, UserDTO, UserFilterDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, IChipsSettingProps, CheckBoxComp } from '../../../shared/components';
import { UserMaintenance } from './UserMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { UserGroupUserAssociate } from './UserGroupUserAssociate';
import { ListId, LocalStorange } from '../../../shared/environment';
import { useTranslation } from 'react-i18next';

export const User: React.FC = () => {

    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [userFilterDTO, setUserFilterDTO] = useState<UserFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [selectRecord, setSelectRecord] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setUserFilterDTO({
            ...userFilterDTO,
            userName: '',
            companyId: '',
            peopleId: '',
            isServiceAccount: false
        } as UserFilterDTO);
    }, []);

    useEffect(() => {
        handleCompanyGetAll();
        handleUserGetAllViewAsync();

        handleGetPermission(ListId.authsphere.userId, setAccessRolePermissionDTO);
    }, [userFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleUserGetAllViewAsync();
    }, [actionExecuted]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList);
    }, []);
    
    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleUserGetAllViewAsync = useCallback(async () => {
        if (userFilterDTO === undefined)
            return;
        if(accessRolePermissionDTO?.allowGetAllView == undefined)
            return; 
        if(!accessRolePermissionDTO?.allowGetAllView) {
            showMessage(t('message.allowGetAllView'), 'info');
            return;
        }
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, true);
    }, [userFilterDTO, accessRolePermissionDTO]);

    const handlePasswordReset = (data: any) => {
        const dto: UserDTO = {
            userName: data.email
        };

        GenericService.PostGenericAsync1('authsphere.user', 'passwordresetasync',dto , setActionExecuted, setLoading, undefined);
    };

    const handleClearFilterClick = () => {
        setUserFilterDTO({
            ...userFilterDTO,
            userName: '',
            companyId: '',
            peopleId: '',
            isServiceAccount: false
        } as UserFilterDTO);
    };

    function handleSearchClick() {
        handleUserGetAllViewAsync();
    }

    const handleNew = () => {
        if(!accessRolePermissionDTO?.allowAdd) {
            showMessage(t('message.allowAdd'), 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        if(!accessRolePermissionDTO?.allowGetById) {
            showMessage(t('message.allowGetById'), 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        if(!accessRolePermissionDTO?.allowDelete) {
            showMessage(t('message.allowDelete'), 'info');
            return;
        }
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleAssociateUsers = (data: string) => {
        setNewRecord('');
        setDeleteRecord('');
        setEditRecord('');
        setSelectRecord(data);
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        // { id: 'new', text: t('user.button.new'), icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: t('user.cardActions.viewDetails'), icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: t('user.cardActions.delete'), icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
        { id: 'associateUsers', toolTip: t('user.cardActions.associateUsers'), icon: 'group_outlined', iconColor: 'info', setAction: handleAssociateUsers },
        { id: 'passwordReset', toolTip: t('user.cardActions.passwordReset'), icon: 'lock_reset_outlined', iconColor: 'warning', setAction: handlePasswordReset },
    ];

    const chipFilters: IChipFilter[] = [
        { label: t('user.chipFilters.totalRecords'), value: userList.length },
        { label: t('user.chipFilters.peopleId'), value: userFilterDTO?.peopleId },
        { label: t('user.chipFilters.userName'), value: userFilterDTO?.userName },
        { label: t('user.chipFilters.company'), value: userFilterDTO?.companyId },
        { label: t('user.chipFilters.isServiceAccount'), value: userFilterDTO?.companyId },
    ];

    const chipStatus : IChipsSettingProps[] = [
        { value: 'true', iconName: 'task_alt_outlined', iconColor: 'success' },
        { value: 'false', iconName: 'block_outlined', iconColor: 'error' },
    ];

    const chipFisrtAccess : IChipsSettingProps[] = [
        { value: 'true', iconName: 'task_alt_outlined', iconColor: 'warning' },
        { value: 'false', iconName: 'block_outlined', iconColor: 'success' },
    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: t('user.columns.name'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'userName', label: t('user.columns.userName'), size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'companyId', label: t('user.columns.company'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'isActive', label: t('user.columns.isActive'), size: 1, type: 'boolean', visible: true, isChip: true, chipSettings: chipStatus, hasFieldLine: false, hasToolTip: true },
        { field: 'isFirstAccess', label: t('user.columns.isFirstAccess'), size: 1, type: 'boolean', visible: true, isChip: true, chipSettings: chipFisrtAccess, hasFieldLine: false, hasToolTip: true },
        { field: 'isAutomaticRenovation', label: t('user.columns.isAutomaticRenovation'), size: 1, type: 'boolean', visible: true, isChip: true, chipSettings: chipStatus, hasFieldLine: false, hasToolTip: true },
        { field: 'accessDeadlineDateTime', label: t('user.columns.accessDeadlineDateTime'), size: 1, type: 'date', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }
    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='USER'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='peopleId'
                                label={t('user.filter.peopleId')}
                                placeholder={'People Id'}
                                setValue={setUserFilterDTO}
                                value={userFilterDTO?.peopleId}
                                obj={userFilterDTO}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='userName'
                                label={t('user.filter.userName')}
                                placeholder={'User Name'}
                                setValue={setUserFilterDTO}
                                value={userFilterDTO?.userName}
                                obj={userFilterDTO}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='companyId'
                                label={t('user.filter.company')}
                                setValue={setUserFilterDTO}
                                value={userFilterDTO?.companyId}
                                obj={userFilterDTO}
                                valueList={companyList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <CheckBoxComp
                                id='isServiceAccount'
                                label={t('user.filter.isServiceAccount')}
                                setValue={setUserFilterDTO}
                                objDTO={userFilterDTO}
                                defaultChecked={userFilterDTO?.isServiceAccount}
                                disabled={false}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={userList}
                        actions={cardActions}
                        hasChildren={false}
                        setRowExpanded={() => void undefined}
                        cardSize={12}
                    />
                </Box>
                {newRecord && (
                    <UserMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <UserMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'authsphere.user'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />
                )}
                {selectRecord && (
                    <UserGroupUserAssociate
                        data={selectRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        actionExecuted={actionExecuted}
                    />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};